/**
 * Gestion des sociétés du tenant
 *
 * ISO 27001 A.9.2.1 - Gestion des accès utilisateurs par société
 * Permet aux Tenant OWNER/ADMIN de créer, modifier, supprimer des sociétés
 */

import {
  Plus,
  Trash2,
  Edit3,
  Building2,
  X,
  MapPin,
  Phone,
  Mail,
  FileText,
  CheckCircle,
  Search,
  ChevronRight,
} from 'lucide-react';
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { notify } from '@/stores/notificationStore';
import { confirm } from '@/stores/confirmStore';
import { companiesAPI } from '@/utils/api';
import { Button, Spinner, Badge } from '@/components/ui-v2';
import { logger } from '@/utils/logger';
import { useTradeReferences } from '@/hooks/useTradeReferences';
import { useSiretSearch, type CompanyData } from '@/hooks/useSiretSearch';
import type { BusinessType } from '@/types/models';
interface Company {
  id: string;
  legalName: string;
  tradeName: string | null;
  registrationNumber: string | null;
  vatNumber: string | null;
  apeCode: string | null;
  businessType: BusinessType | null;
  companyTrade: string | null;
  addressLine1: string | null;
  zipCode: string | null;
  city: string | null;
  email: string | null;
  phone: string | null;
  logoUrl: string | null;
}

interface NewCompany {
  legalName: string;
  tradeName: string;
  registrationNumber: string;
  vatNumber: string;
  apeCode: string;
  businessType: BusinessType;
  companyTrade: string;
  addressLine1: string;
  zipCode: string;
  city: string;
  countryCode: string;
  email: string;
  phone: string;
}

interface QuotaInfo {
  current: number;
  limit: number;
  canCreate: boolean;
}

export const CompaniesManagement: React.FC = () => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const { data: companies = [], isLoading: loading } = useQuery<Company[]>({
    queryKey: ['companies', 'list'],
    queryFn: async () => {
      const data = await companiesAPI.getAll();
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60 * 1000,
  });

  const { data: quota = null } = useQuery<QuotaInfo | null>({
    queryKey: ['companies', 'quota'],
    queryFn: async () => {
      try {
        const myCompaniesData = await companiesAPI.getMyCompanies();
        return myCompaniesData.quota || null;
      } catch (error) {
        logger.warn('Failed to fetch company quota', error);
        return null;
      }
    },
    staleTime: 60 * 1000,
  });

  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [saving, setSaving] = useState(false);
  const { categories: tradeCategories, isLoading: tradesLoading } = useTradeReferences();
  const { searchBySiret, searchByName, clearResults, loading: searchLoading, results: searchResults } = useSiretSearch();
  const [searchMode, setSearchMode] = useState<'name' | 'siret'>('name');
  const [searchQuery, setSearchQuery] = useState('');

  const [newCompany, setNewCompany] = useState<NewCompany>({
    legalName: '',
    tradeName: '',
    registrationNumber: '',
    vatNumber: '',
    apeCode: '',
    businessType: 'SERVICE',
    companyTrade: '',
    addressLine1: '',
    zipCode: '',
    city: '',
    countryCode: 'FR',
    email: '',
    phone: '',
  });

  // Card classes V2
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm`;

  // 🔍 Recherche entreprise INPI/SIRENE
  const handleCompanySearch = async () => {
    if (searchMode === 'name') {
      await searchByName(searchQuery);
    } else {
      const result = await searchBySiret(searchQuery);
      if (result) {
        selectSearchResult(result);
      }
    }
  };

  const selectSearchResult = (company: CompanyData) => {
    setNewCompany((prev) => ({
      ...prev,
      legalName: company.name,
      addressLine1: company.address,
      city: company.city,
      zipCode: company.postalCode,
      registrationNumber: company.siret,
      vatNumber: company.tvaNumber,
      apeCode: company.apeCode || prev.apeCode,
      email: company.email || prev.email,
      phone: company.phone || prev.phone,
    }));
    clearResults();
    setSearchQuery('');
  };

  const handleAddCompany = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newCompany.legalName || !newCompany.registrationNumber) {
      notify.warning(t('admin.companies.requiredFields'), t('admin.companies.missingFields'));
      return;
    }

    try {
      setSaving(true);
      await companiesAPI.create(newCompany);
      notify.success(t('admin.companies.createdSuccess'), t('admin.companies.creationSuccess'));
      setShowAddCompany(false);
      setNewCompany({
        legalName: '',
        tradeName: '',
        registrationNumber: '',
        vatNumber: '',
        apeCode: '',
        businessType: 'SERVICE',
        companyTrade: '',
        addressLine1: '',
        zipCode: '',
        city: '',
        countryCode: 'FR',
        email: '',
        phone: '',
      });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      // Gestion spéciale de l'erreur 402 (quota atteint)
      if (message.includes('Quota') || message.includes('quota')) {
        notify.warning(
          message || t('admin.companies.limitReached'),
          t('admin.companies.limitReached')
        );
        // Recharger pour mettre à jour le quota
        queryClient.invalidateQueries({ queryKey: ['companies'] });
      } else {
        notify.error(message || t('admin.companies.errorLabel'), t('admin.companies.errorLabel'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setShowEditCompany(true);
  };

  const handleUpdateCompany = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCompany) return;

    try {
      setSaving(true);
      await companiesAPI.update(selectedCompany.id, {
        legalName: selectedCompany.legalName,
        tradeName: selectedCompany.tradeName,
        registrationNumber: selectedCompany.registrationNumber,
        vatNumber: selectedCompany.vatNumber,
        apeCode: selectedCompany.apeCode,
        businessType: selectedCompany.businessType,
        companyTrade: selectedCompany.companyTrade || undefined,
        addressLine1: selectedCompany.addressLine1,
        zipCode: selectedCompany.zipCode,
        city: selectedCompany.city,
        email: selectedCompany.email,
        phone: selectedCompany.phone,
      });
      notify.success(t('admin.companies.updatedSuccess'), t('admin.companies.updateSuccess'));
      setShowEditCompany(false);
      setSelectedCompany(null);
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      notify.error(message || t('admin.companies.errorLabel'), t('admin.companies.errorLabel'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async (company: Company) => {
    const confirmed = await confirm({
      title: t('admin.companies.deleteTitle'),
      message: t('admin.companies.deleteMessage', { name: company.legalName }),
      confirmText: t('common.actions.delete'),
      cancelText: t('common.actions.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await companiesAPI.delete(company.id);
      notify.success(t('admin.companies.deletedSuccess'), t('admin.companies.deletionSuccess'));
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      notify.error(message || t('admin.companies.errorLabel'), t('admin.companies.errorLabel'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Building2 className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('admin.companies.title')}</h2>
            <p className="text-gray-500 text-sm">
              {t('admin.companies.subtitle')}
              {quota && quota.limit !== -1 && (
                <span className={`ml-2 font-medium ${quota.canCreate ? 'text-teal-600' : 'text-amber-600'}`}>
                  · {quota.current}/{quota.limit} {t('admin.companies.used')}
                </span>
              )}
            </p>
          </div>
        </div>
        <span title={quota && !quota.canCreate ? `${t('admin.companies.limitReached')} (${quota.current}/${quota.limit})` : undefined}>
          <Button
            onClick={() => setShowAddCompany(true)}
            disabled={quota !== null && !quota.canCreate}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.companies.newCompany')}
          </Button>
        </span>
      </div>

      {/* Companies Grid */}
      {companies.length === 0 ? (
        <div className={cardClasses}>
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('admin.companies.noCompanies')}</h3>
            <p className="text-gray-500 mb-4">{t('admin.companies.noCompaniesDesc')}</p>
            <Button
              onClick={() => setShowAddCompany(true)}
              disabled={quota !== null && !quota.canCreate}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('admin.companies.createCompany')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map((company) => (
            <div key={company.id} className={cardClasses}>
              {/* Company Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {company.logoUrl ? (
                    <img
                      src={company.logoUrl}
                      alt={company.legalName}
                      className="w-12 h-12 rounded-xl object-contain bg-white"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-teal-600" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900">{company.legalName}</h3>
                    {company.tradeName && company.tradeName !== company.legalName && (
                      <p className="text-sm text-gray-500">{company.tradeName}</p>
                    )}
                    {company.businessType && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-teal-100 text-teal-700 rounded-full">
                        {t(`admin.companies.businessTypes.${company.businessType}`)}
                      </span>
                    )}
                  </div>
                </div>
                <Badge variant="success">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {t('admin.companies.active')}
                </Badge>
              </div>

              {/* Company Info */}
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                {company.registrationNumber && (
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span>SIRET: {company.registrationNumber}</span>
                  </div>
                )}
                {company.city && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>{company.zipCode} {company.city}</span>
                  </div>
                )}
                {company.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{company.email}</span>
                  </div>
                )}
                {company.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{company.phone}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditCompany(company)}
                >
                  <Edit3 className="w-4 h-4 mr-1" />
                  {t('common.actions.edit')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDeleteCompany(company)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t('common.actions.delete')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Add Company */}
      {showAddCompany && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl border border-white/40 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-teal-50/50 to-white border-b border-gray-100 flex items-center justify-between rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-xl">
                  <Building2 className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{t('admin.companies.newCompanyTitle')}</h3>
              </div>
              <button
                onClick={() => setShowAddCompany(false)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleAddCompany} className="p-6 space-y-6">
              {/* 🔍 Recherche entreprise INPI/SIRENE */}
              <div className="p-5 bg-white/40 border border-white/50 rounded-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Search className="w-4 h-4 text-teal-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">{t('admin.companies.companySearch.title')}</h3>
                </div>

                {/* Toggle: Recherche par Nom / SIRET */}
                <div className="grid grid-cols-2 gap-2 p-1.5 bg-gray-100 rounded-xl mb-4">
                  <button
                    type="button"
                    onClick={() => { setSearchMode('name'); setSearchQuery(''); clearResults(); }}
                    className={`py-2 px-4 text-sm font-medium rounded-lg transition-all duration-300 ${
                      searchMode === 'name'
                        ? 'bg-white text-teal-700 shadow-md'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    {t('admin.companies.companySearch.byName')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSearchMode('siret'); setSearchQuery(''); clearResults(); }}
                    className={`py-2 px-4 text-sm font-medium rounded-lg transition-all duration-300 ${
                      searchMode === 'siret'
                        ? 'bg-white text-teal-700 shadow-md'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    {t('admin.companies.companySearch.bySiret')}
                  </button>
                </div>

                <div className="flex gap-3">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      const value = searchMode === 'siret'
                        ? e.target.value.replace(/\s/g, '').slice(0, 14)
                        : e.target.value;
                      setSearchQuery(value);
                    }}
                    placeholder={searchMode === 'name' ? t('admin.companies.companySearch.namePlaceholder') : t('admin.companies.companySearch.siretPlaceholder')}
                    className="flex-1 px-4 py-3 text-sm bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCompanySearch();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleCompanySearch}
                    disabled={searchLoading || !searchQuery.trim()}
                    className="px-5 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center gap-2"
                  >
                    {searchLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        {t('admin.companies.companySearch.searching')}
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4" />
                        {t('admin.companies.companySearch.searchButton')}
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  {searchMode === 'name'
                    ? t('admin.companies.companySearch.searchByNameHint')
                    : t('admin.companies.companySearch.searchBySiretHint')}
                </p>

                {/* Résultats de recherche */}
                {searchMode === 'name' && searchResults.length > 0 && (
                  <div className="mt-4 bg-white border border-gray-200 rounded-2xl p-4 max-h-64 overflow-y-auto">
                    <p className="text-sm font-medium text-gray-900 mb-3">
                      {t('admin.companies.companySearch.resultCount', { count: searchResults.length })}
                    </p>
                    <div className="space-y-2">
                      {searchResults.map((company, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => selectSearchResult(company)}
                          className="w-full text-left p-4 bg-gray-50 border border-gray-100 rounded-xl hover:border-teal-400 hover:bg-white hover:shadow-md transition-all duration-300 group"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate group-hover:text-teal-700 transition-colors">
                                {company.name}
                              </p>
                              <p className="text-sm text-gray-600 truncate">
                                {company.address}, {company.postalCode} {company.city}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                SIRET: {company.siret}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Identité */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">
                  {t('admin.companies.companyIdentity')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.legalName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newCompany.legalName}
                      onChange={(e) => setNewCompany({ ...newCompany, legalName: e.target.value })}
                      placeholder={t('admin.companies.placeholders.legalName')}
                      className={inputClasses}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.tradeName')}
                    </label>
                    <input
                      type="text"
                      value={newCompany.tradeName}
                      onChange={(e) => setNewCompany({ ...newCompany, tradeName: e.target.value })}
                      placeholder={t('admin.companies.placeholders.tradeName')}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.siret')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newCompany.registrationNumber}
                      onChange={(e) => setNewCompany({ ...newCompany, registrationNumber: e.target.value })}
                      placeholder={t('admin.companies.placeholders.siret')}
                      className={inputClasses}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.vatNumber')}
                    </label>
                    <input
                      type="text"
                      value={newCompany.vatNumber}
                      onChange={(e) => setNewCompany({ ...newCompany, vatNumber: e.target.value })}
                      placeholder={t('admin.companies.placeholders.vatNumber')}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.apeCode')}
                    </label>
                    <input
                      type="text"
                      value={newCompany.apeCode}
                      onChange={(e) => setNewCompany({ ...newCompany, apeCode: e.target.value })}
                      placeholder="Ex: 4399C"
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.businessType')}
                    </label>
                    <select
                      value={newCompany.businessType}
                      onChange={(e) => setNewCompany({ ...newCompany, businessType: e.target.value as BusinessType })}
                      className={inputClasses}
                    >
                      {(['SERVICE', 'COMMERCE', 'ARTISAN_BTP', 'PME_BTP', 'LIBERAL', 'MIXTE'] as const).map((bt) => (
                        <option key={bt} value={bt}>
                          {t(`admin.companies.businessTypes.${bt}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.companyTrade')}
                    </label>
                    <select
                      value={newCompany.companyTrade}
                      onChange={(e) => {
                        const tradeCode = e.target.value;
                        const trade = tradeCategories.flatMap((c) => c.trades).find((tr) => tr.code === tradeCode);
                        setNewCompany({
                          ...newCompany,
                          companyTrade: tradeCode,
                          ...(trade ? { businessType: trade.businessType as BusinessType } : {}),
                        });
                      }}
                      className={inputClasses}
                      disabled={tradesLoading}
                    >
                      <option value="">{t('admin.companies.companyTradePlaceholder')}</option>
                      {tradeCategories.map((cat) => (
                        <optgroup key={cat.category} label={cat.category}>
                          {cat.trades.map((trade) => (
                            <option key={trade.code} value={trade.code}>
                              {trade.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Adresse */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">
                  {t('admin.companies.address')}
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.address')}
                    </label>
                    <input
                      type="text"
                      value={newCompany.addressLine1}
                      onChange={(e) => setNewCompany({ ...newCompany, addressLine1: e.target.value })}
                      placeholder={t('admin.companies.placeholders.address')}
                      className={inputClasses}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('admin.companies.zipCode')}
                      </label>
                      <input
                        type="text"
                        value={newCompany.zipCode}
                        onChange={(e) => setNewCompany({ ...newCompany, zipCode: e.target.value })}
                        placeholder={t('admin.companies.placeholders.zipCode')}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('admin.companies.city')}
                      </label>
                      <input
                        type="text"
                        value={newCompany.city}
                        onChange={(e) => setNewCompany({ ...newCompany, city: e.target.value })}
                        placeholder={t('admin.companies.placeholders.city')}
                        className={inputClasses}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">
                  {t('admin.companies.contact')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.email')}
                    </label>
                    <input
                      type="email"
                      value={newCompany.email}
                      onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })}
                      placeholder={t('admin.companies.placeholders.email')}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.phone')}
                    </label>
                    <input
                      type="tel"
                      value={newCompany.phone}
                      onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })}
                      placeholder={t('admin.companies.placeholders.phone')}
                      className={inputClasses}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowAddCompany(false)}
                >
                  {t('common.actions.cancel')}
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? <Spinner size="sm" className="mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  {t('admin.companies.createSubmit')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Edit Company */}
      {showEditCompany && selectedCompany && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl border border-white/40 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-teal-50/50 to-white border-b border-gray-100 flex items-center justify-between rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-xl">
                  <Edit3 className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{t('admin.companies.editCompanyTitle')}</h3>
              </div>
              <button
                onClick={() => { setShowEditCompany(false); setSelectedCompany(null); }}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleUpdateCompany} className="p-6 space-y-6">
              {/* Identité */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">
                  {t('admin.companies.companyIdentity')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.legalName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={selectedCompany.legalName}
                      onChange={(e) => setSelectedCompany({ ...selectedCompany, legalName: e.target.value })}
                      className={inputClasses}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.tradeName')}
                    </label>
                    <input
                      type="text"
                      value={selectedCompany.tradeName || ''}
                      onChange={(e) => setSelectedCompany({ ...selectedCompany, tradeName: e.target.value })}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.siret')}
                    </label>
                    <input
                      type="text"
                      value={selectedCompany.registrationNumber || ''}
                      onChange={(e) => setSelectedCompany({ ...selectedCompany, registrationNumber: e.target.value })}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.vatNumberShort')}
                    </label>
                    <input
                      type="text"
                      value={selectedCompany.vatNumber || ''}
                      onChange={(e) => setSelectedCompany({ ...selectedCompany, vatNumber: e.target.value })}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.apeCode')}
                    </label>
                    <input
                      type="text"
                      value={selectedCompany.apeCode || ''}
                      onChange={(e) => setSelectedCompany({ ...selectedCompany, apeCode: e.target.value })}
                      placeholder="Ex: 4399C"
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.businessType')}
                    </label>
                    <select
                      value={selectedCompany.businessType || 'SERVICE'}
                      onChange={(e) => setSelectedCompany({ ...selectedCompany, businessType: e.target.value as BusinessType })}
                      className={inputClasses}
                    >
                      {(['SERVICE', 'COMMERCE', 'ARTISAN_BTP', 'PME_BTP', 'LIBERAL', 'MIXTE'] as const).map((bt) => (
                        <option key={bt} value={bt}>
                          {t(`admin.companies.businessTypes.${bt}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.companyTrade')}
                    </label>
                    <select
                      value={selectedCompany.companyTrade || ''}
                      onChange={(e) => {
                        const tradeCode = e.target.value;
                        const trade = tradeCategories.flatMap((c) => c.trades).find((tr) => tr.code === tradeCode);
                        setSelectedCompany({
                          ...selectedCompany,
                          companyTrade: tradeCode || null,
                          ...(trade ? { businessType: trade.businessType as BusinessType } : {}),
                        });
                      }}
                      className={inputClasses}
                      disabled={tradesLoading}
                    >
                      <option value="">{t('admin.companies.companyTradePlaceholder')}</option>
                      {tradeCategories.map((cat) => (
                        <optgroup key={cat.category} label={cat.category}>
                          {cat.trades.map((trade) => (
                            <option key={trade.code} value={trade.code}>
                              {trade.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Adresse */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">
                  {t('admin.companies.address')}
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.address')}
                    </label>
                    <input
                      type="text"
                      value={selectedCompany.addressLine1 || ''}
                      onChange={(e) => setSelectedCompany({ ...selectedCompany, addressLine1: e.target.value })}
                      className={inputClasses}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('admin.companies.zipCode')}
                      </label>
                      <input
                        type="text"
                        value={selectedCompany.zipCode || ''}
                        onChange={(e) => setSelectedCompany({ ...selectedCompany, zipCode: e.target.value })}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('admin.companies.city')}
                      </label>
                      <input
                        type="text"
                        value={selectedCompany.city || ''}
                        onChange={(e) => setSelectedCompany({ ...selectedCompany, city: e.target.value })}
                        className={inputClasses}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">
                  {t('admin.companies.contact')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.email')}
                    </label>
                    <input
                      type="email"
                      value={selectedCompany.email || ''}
                      onChange={(e) => setSelectedCompany({ ...selectedCompany, email: e.target.value })}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.companies.phone')}
                    </label>
                    <input
                      type="tel"
                      value={selectedCompany.phone || ''}
                      onChange={(e) => setSelectedCompany({ ...selectedCompany, phone: e.target.value })}
                      className={inputClasses}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setShowEditCompany(false); setSelectedCompany(null); }}
                >
                  {t('admin.companies.cancel')}
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? <Spinner size="sm" className="mr-2" /> : <Edit3 className="w-4 h-4 mr-2" />}
                  {t('common.actions.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesManagement;
